import deepmerge from 'deepmerge';
import { omit, camelCase } from 'lodash';
import buildParamsUrl from './buildParamsUrl';
import cleanMetadataKeys from './cleanMetadataKeys';

function pathForKey(...parts) {
  return parts.join('/')
    .split('/')
    .filter(k => !/[:*]/.test(k))
    .filter((_, i) => i !== 0);
}

function buildNestedRoute(node, {
  prefixFn,
  currentPath = [],
  currentMetadata = {},
  appendVersion = null,
}) {
  const isRoot = currentPath.length === 0;
  let pathsToAppendVersionTo = [];

  if (isRoot) {
    const versionsByBaseName = Object.keys(node).reduce((_all, path) => {
      const all = { ..._all };

      // remove param placeholders to create the basename
      const baseName = path.split('/').filter(
        p => p.substr(0, 1) !== ':',
      )
        .slice(1)
        .join('');

      all[baseName] = (all[baseName] || []);
      all[baseName].push(path);

      return all;
    }, {});

    Object.keys(versionsByBaseName).forEach((baseName) => {
      if (versionsByBaseName[baseName].length > 1) {
        pathsToAppendVersionTo = pathsToAppendVersionTo.concat(
          versionsByBaseName[baseName],
        );
      }
    });
  }

  return Object.keys(node).reduce((all, path) => {
    const newAll = { ...all };

    if (typeof (node[path]) !== 'object') {
      throw new Error(`${node[path]} is not an object! check your routes config.`);
    }

    const version = path.split('/')[0];
    const { actions, metadata } = node[path];
    const rootMetadata = deepmerge(currentMetadata, metadata || {});

    const rootShouldAppendVersion = pathsToAppendVersionTo.indexOf(path) !== -1;

    if (actions) {
      actions.forEach(([actionPath, method, _metadata]) => {
        let keyParts = pathForKey(...currentPath, path, actionPath).join('_');
        if (appendVersion || rootShouldAppendVersion) {
          keyParts = `${keyParts}_${appendVersion || version}`;
        }

        const actionKey = camelCase(keyParts);

        let pathBase = [...currentPath, path];
        if (actionPath.indexOf('!') !== 0) {
          pathBase.push(actionPath);
        }

        pathBase = pathBase.join('/');

        const pathFn = params => buildParamsUrl(prefixFn(pathBase), params);
        const actionMetadata = cleanMetadataKeys(deepmerge(rootMetadata, _metadata || {}));

        newAll[actionKey] = {
          method,
          path: pathFn,
          metadata: actionMetadata,
        };
      });
    }

    return {
      ...newAll,
      ...buildNestedRoute(
        omit(node[path], 'actions', 'metadata'),
        {
          prefixFn,
          currentPath: [...currentPath, path],
          currentMetadata: rootMetadata,
          appendVersion: appendVersion || (rootShouldAppendVersion && version),
        },
      ),
    };
  }, {});
}

/**
 * Build URL information from a config file and a prefix function
 * for use by XHR functions to fetch data from an API.
 */
export default function jsApi(routes, prefixFn) {
  return buildNestedRoute(routes, { prefixFn });
}
